import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ChartKey,
  ITrendChartsFilters,
  TAttributeUnits,
  TIME_GRANULARITY,
  TTrendChartResponseType,
  TWheatColorByCode,
  TWheatColorById,
} from '../../types/models/DataAnalysis'
import { RootState } from 'store/store'
import { getFetcherForChart } from 'services/dataAnalysisService'
import { getDefaultDateRange } from '../../pages/DataAnalysis/utils/consts'

export type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

export interface IBaseFilters {
  wheat_codes?: string[]
  bins?: string[]
  blend_types?: string[]
  date_range?: { from: string; to: string }
  time_granularity?: TIME_GRANULARITY
  [key: string]: any
}

export interface IChartState {
  draft: IBaseFilters | null
  applied: IBaseFilters | null
  data: any
  status: LoadStatus
  error: string | null
  lastAppliedAt?: string
}

// Save here charts full data by key and wheat colors generated for trend chart colors.
interface IDataAnalysisState {
  byKey: Partial<Record<ChartKey, IChartState>>
  wheatColors: TWheatColorsState
  units: TAttributeUnits
  globalFilters: {
    isApplyGlobally: boolean
    draft: IBaseFilters | null
    applied: IBaseFilters | null
  }
}

const initialChartState: IChartState = {
  draft: null,
  applied: null,
  data: null,
  status: 'idle',
  error: null,
}

type TWheatColorsState = {
  byId: TWheatColorById // id -> "#hex"
  byCode: TWheatColorByCode // "401" -> "#hex"
}

const initialWheatColorsState: TWheatColorsState = {
  byId: {},
  byCode: {},
}

const initialState: IDataAnalysisState = {
  byKey: {},
  wheatColors: initialWheatColorsState,
  units: {},
  globalFilters: {
    isApplyGlobally: true,
    draft: null,
    applied: null,
  },
}

// Guarantees that a chart entry exists for the given key.
// If it doesn’t exist yet -> it creates it with a fresh copy of initialChartState.
function ensureChart(state: IDataAnalysisState, key: ChartKey) {
  if (!state.byKey[key]) state.byKey[key] = { ...initialChartState }
  return state.byKey[key]
}

// Async action to Check if filters (draft) exist for a chart.
// Fetch chart data from the right API.
// Normalize the response into a standard format.
// Return data or error -> handled automatically in reducers.
export const applyAndFetchChart = createAsyncThunk(
  'dataAnalysis/applyAndFetchChart',
  async ({ key }: { key: ChartKey }, { getState, rejectWithValue }) => {
    const state = getState() as RootState
    const chart = state.dataAnalysis.byKey[key]
    const { isApplyGlobally, draft: globalDraft } =
      state.dataAnalysis.globalFilters

    if ((!chart || !chart.draft) && (!isApplyGlobally || !globalDraft)) {
      return rejectWithValue({ key, message: 'No filters to apply' })
    }

    let filtersToApply: IBaseFilters | null = null

    if (isApplyGlobally && globalDraft) {
      // Use global filters but ensure we don't send incompatible filters
      filtersToApply = { ...globalDraft }

      const requiresWheatCodes =
        key.includes('parameters') ||
        key.includes('consistency') ||
        key.includes('bin') ||
        key.includes('storage')

      const requiresProducts = key.includes('cost')

      const requiresSingleProduct = key.includes('compositions')

      // Charts that require time granularity
      const requiresTimeGranularity =
        key.includes('parameters_trend') ||
        key.includes('cost_trends') ||
        key.includes('compositions')

      // Keep only compatible filters
      if (requiresWheatCodes) {
        delete filtersToApply.blend_types
        delete filtersToApply.blend_type
      } else if (requiresProducts) {
        delete filtersToApply.wheat_codes
      } else if (requiresSingleProduct) {
        delete filtersToApply.wheat_codes
        delete filtersToApply.blend_types
      }

      // Ensure time_granularity is set for charts that require it
      if (requiresTimeGranularity) {
        // Use the chart's time_granularity if available, otherwise default to DAILY
        if (chart && chart.draft && chart.draft.time_granularity) {
          filtersToApply.time_granularity = chart.draft.time_granularity
        } else if (!filtersToApply.time_granularity) {
          filtersToApply.time_granularity = TIME_GRANULARITY.DAILY
        }
      }

      // When global filters are applied, we ignore local filters completely
      // Local filters are only used when isApplyGlobally is false
    } else if (chart && chart.draft) {
      filtersToApply = chart.draft
    }

    // Ensure we always send a valid date_range. In some cases the UI shows a default
    // range but the global/local draft in the store might not yet include it until
    // the user changes the date manually. That caused the first Apply in global mode
    // to miss date_range. Fallback to centralized default (last month -> yesterday) if missing.
    if (filtersToApply) {
      const hasFrom = Boolean(filtersToApply.date_range?.from)
      const hasTo = Boolean(filtersToApply.date_range?.to)
      if (!hasFrom || !hasTo) {
        filtersToApply.date_range = getDefaultDateRange()
      }
    }

    const fetcher = getFetcherForChart(key) as (f: IBaseFilters) => Promise<any>
    try {
      const res = await fetcher(filtersToApply!)
      return { key, data: res.data }
    } catch (e: any) {
      return rejectWithValue({ key, message: e?.message ?? 'Request failed' })
    }
  }
)

const dataAnalysisSlice = createSlice({
  name: 'dataAnalysis',
  initialState,
  reducers: {
    setDraftFilters(
      state,
      action: PayloadAction<{ key: ChartKey; filters: ITrendChartsFilters }>
    ) {
      const { key, filters } = action.payload
      const chart = ensureChart(state, key)
      chart.draft = filters
    },
    // Hydrate draft filters for the currently active context (global vs local)
    // without touching applied or data. Use after discarding drafts on tab switch.
    hydrateDraftsForActiveTab(state, action: PayloadAction<{ key: ChartKey }>) {
      const { key } = action.payload
      if (state.globalFilters.isApplyGlobally) {
        // Surface last applied global filters into the draft (if any)
        state.globalFilters.draft = state.globalFilters.applied
          ? { ...state.globalFilters.applied }
          : null
      } else {
        // Hydrate only the active chart's draft from its last applied
        const chart = ensureChart(state, key)
        chart.draft = chart.applied ? { ...chart.applied } : null
      }
    },
    patchDraftFilters(
      state,
      action: PayloadAction<{
        key: ChartKey
        patch: Partial<ITrendChartsFilters>
      }>
    ) {
      const { key, patch } = action.payload
      const chart = ensureChart(state, key)
      chart.draft = {
        ...(chart.draft ?? ({} as ITrendChartsFilters)),
        ...patch,
      }
    },
    setGlobalDraftFilters(state, action: PayloadAction<ITrendChartsFilters>) {
      state.globalFilters.draft = action.payload
    },
    patchGlobalDraftFilters(
      state,
      action: PayloadAction<Partial<ITrendChartsFilters>>
    ) {
      state.globalFilters.draft = {
        ...(state.globalFilters.draft ?? ({} as ITrendChartsFilters)),
        ...action.payload,
      }
    },
    toggleApplyGlobally(state, action: PayloadAction<boolean | undefined>) {
      const nextIsGlobal =
        action.payload !== undefined
          ? action.payload
          : !state.globalFilters.isApplyGlobally

      const wasGlobal = state.globalFilters.isApplyGlobally
      const isChange = nextIsGlobal !== wasGlobal

      // Set the new mode first
      state.globalFilters.isApplyGlobally = nextIsGlobal

      // If the value truly changed, hydrate drafts from the last applied values
      // BEFORE discarding, so we can properly restore the right context
      if (isChange) {
        if (nextIsGlobal) {
          // Going to global: restore the last applied global filters into draft
          state.globalFilters.draft = state.globalFilters.applied
            ? { ...state.globalFilters.applied }
            : null
          // Clear all per-chart drafts when switching to global
          Object.keys(state.byKey).forEach((k) => {
            const key = k as ChartKey
            const chart = ensureChart(state, key)
            chart.draft = null
          })
        } else {
          // Going to local: clear global draft and hydrate each chart draft from its last applied
          state.globalFilters.draft = null
          Object.keys(state.byKey).forEach((k) => {
            const key = k as ChartKey
            const chart = ensureChart(state, key)
            chart.draft = chart.applied ? { ...chart.applied } : null
          })
        }
      } else {
        // No actual toggle change (same value dispatched for cleanup on tab change)
        // Just discard any unsaved (draft) filters without hydration
        state.globalFilters.draft = null
        Object.keys(state.byKey).forEach((k) => {
          const key = k as ChartKey
          const chart = ensureChart(state, key)
          chart.draft = null
        })
      }
    },
    clearChartData(state, action: PayloadAction<{ key: ChartKey }>) {
      const { key } = action.payload
      const chart = ensureChart(state, key)
      chart.data = null
      chart.status = 'idle'
      chart.error = null
    },
    resetChart(state, action: PayloadAction<{ key: ChartKey }>) {
      const { key } = action.payload
      state.byKey[key] = { ...initialChartState }
    },
    setWheatColors(
      state,
      action: PayloadAction<{
        byId: TWheatColorById
        byCode: TWheatColorByCode
      }>
    ) {
      state.wheatColors.byId = action.payload.byId
      state.wheatColors.byCode = action.payload.byCode
    },
    // Ensures each wheat ID is assigned one permanent color (if not already set),
    // and then keeps all wheat codes mapped to that ID in sync with the chosen color.
    upsertWheatColors(
      state,
      action: PayloadAction<Array<{ id: number; code: string; color: string }>>
    ) {
      for (const { id, code, color } of action.payload) {
        if (!state.wheatColors.byId[id]) {
          state.wheatColors.byId[id] = color
        }
        // always reflect the id's chosen color under the code key
        state.wheatColors.byCode[code] = state.wheatColors.byId[id]
      }
    },
    clearWheatColors(state) {
      state.wheatColors = initialWheatColorsState
    },
    // Replace all units at once
    setUnits(state, action: PayloadAction<TAttributeUnits>) {
      state.units = action.payload
    },
    // Merge/patch some units (e.g., when BE sends partial)
    upsertUnits(state, action: PayloadAction<Partial<TAttributeUnits>>) {
      state.units = { ...state.units, ...action.payload }
    },
    clearUnits(state) {
      state.units = {}
    },
    // Discard all unsaved filters (do not touch applied/data)
    discardUnsavedFilters(state) {
      // clear global draft
      state.globalFilters.draft = null
      // clear drafts for all charts
      Object.keys(state.byKey).forEach((k) => {
        const key = k as ChartKey
        const chart = ensureChart(state, key)
        chart.draft = null
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyAndFetchChart.pending, (state, action) => {
        const { key } = action.meta.arg
        const chart = ensureChart(state, key)
        chart.status = 'loading'
        chart.error = null

        if (state.globalFilters.isApplyGlobally && state.globalFilters.draft) {
          // Global Apply: update ONLY the global applied filters.
          // Do NOT touch per-chart applied filters here, so that local saved filters persist
          // and can be restored when the user toggles back to local mode.
          state.globalFilters.applied = { ...state.globalFilters.draft }
        } else {
          chart.applied = chart.draft ? { ...chart.draft } : null
        }

        chart.lastAppliedAt = new Date().toISOString()
      })
      .addCase(applyAndFetchChart.fulfilled, (state, action) => {
        const { key, data } = action.payload as {
          key: ChartKey
          data: TTrendChartResponseType[]
        }
        const chart = ensureChart(state, key)
        chart.status = 'succeeded'
        chart.data = data
      })
      .addCase(applyAndFetchChart.rejected, (state, action: any) => {
        const { key, message } = action.payload || {
          key: action.meta?.arg?.key,
          message: action.error?.message,
        }
        const chart = ensureChart(state, key)
        chart.status = 'failed'
        chart.error = message ?? 'Unknown error'
      })
  },
})

export const {
  setDraftFilters,
  patchDraftFilters,
  clearChartData,
  resetChart,
  setWheatColors,
  upsertWheatColors,
  clearWheatColors,
  setUnits,
  upsertUnits,
  clearUnits,
  setGlobalDraftFilters,
  patchGlobalDraftFilters,
  toggleApplyGlobally,
  discardUnsavedFilters,
  hydrateDraftsForActiveTab,
} = dataAnalysisSlice.actions

export default dataAnalysisSlice.reducer
